import { FC, type JSX } from 'react';

import { productUnits } from '@eneco/enumerations/productUnits';
import { useFormatter } from '@eneco/i18n';
import { Box, Stack, Text } from '@eneco/sparky';
import { ElectricityIcon, GasIcon, RedeliveryIcon, WarmthIcon, WaterIcon } from '@eneco/sparky/icons';
import { Fields as ExperimentFields } from '@sitecore/types/manual/smeFlowExperiment/SMEOfferStep';
import { Fields } from '@sitecore/types/SMEOfferStep';

import { tariffDetailsOverviewCopy } from './tariffDetailsOverview.copy';
import { FlowContextSme } from '../../context';
import { checkOfferIncludesDynamicPricingProduct } from '../../helpers';
import { Offer } from '../../types';
import { NettingDisclosureNote } from '../NettingDisclosure/NettingDisclosure';
import { NettingSplitFeature } from '../NettingDisclosure/NettingSplitFeature';

interface Props {
  offer?: Offer;
  textualData?: Fields | ExperimentFields;
  flowContext?: FlowContextSme;
}

export const TariffDetailsOverview: FC<Props> = ({ textualData, offer, flowContext }): JSX.Element => {
  const { isSME } = flowContext ?? {};
  const { costDetails, usages } = offer ?? {};

  const {
    electricityDeliveryNormal = 0,
    electricityDeliveryLow = 0,
    electricityRedeliveryRemainder = 0,
    gas = 0,
    warmthElectricity = 0,
    warmthWater = 0,
  } = usages || {};

  const { currency } = useFormatter();

  const costsType = isSME ? 'vatExcluded' : 'vatIncluded';

  return (
    <Text size={{ initial: 'BodyXS', md: 'BodyS' }}>
      <Stack gap={{ initial: '1', md: '3' }}>
        {(electricityDeliveryNormal || electricityDeliveryLow) && (
          <>
            <Stack.Item>
              <Stack direction="row" alignY="start" gap="2">
                <Stack.Item grow={true}>
                  <Stack direction="row" alignY="center" gap="2">
                    <ElectricityIcon color="iconElectricity" />
                    <Text weight="bold">
                      {Number(electricityDeliveryLow) > 0
                        ? textualData?.unitPriceLabels?.electricityHighLabel?.value
                        : textualData?.unitPriceLabels?.electricityLabel?.value}
                    </Text>
                  </Stack>
                </Stack.Item>

                <Stack.Item shrink={false}>
                  <Stack direction="row" alignY="end" gap="2">
                    <Stack.Item grow={true}>
                      {`${currency.euroHighPrecision(
                        costDetails?.electricityCostDetails?.tariff?.price?.[costsType],
                      )} / ${productUnits.electricity}`}
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
              </Stack>
            </Stack.Item>

            {Number(electricityDeliveryLow) > 0 && (
              <Stack.Item>
                <Stack direction="row" alignY="start" gap="2">
                  <Stack.Item grow={true}>
                    <Stack direction="row" alignY="center" gap="2">
                      <ElectricityIcon color="iconElectricity" />
                      <Text weight="bold">{textualData?.unitPriceLabels?.electricityLowLabel?.value}</Text>
                    </Stack>
                  </Stack.Item>

                  <Stack.Item shrink={false}>
                    <Stack direction="row" alignY="end" gap="2">
                      <Stack.Item grow={true}>
                        {`${currency.euroHighPrecision(
                          costDetails?.electricityCostDetails?.lowtariff?.price?.[costsType],
                        )} / ${productUnits.electricity}`}
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                </Stack>
              </Stack.Item>
            )}
          </>
        )}

        {Number(gas) > 0 && (
          <Stack.Item>
            <Stack direction="row" alignY="start" gap="2">
              <Stack.Item grow={true}>
                <Stack direction="row" alignY="center" gap="2">
                  <GasIcon color="iconGas" />
                  <Text weight="bold">{textualData?.unitPriceLabels?.gasLabel?.value}</Text>
                </Stack>
              </Stack.Item>

              <Stack.Item shrink={false}>
                <Stack direction="row" alignY="end" gap="2">
                  <Stack.Item grow={true}>
                    {`${currency.euroHighPrecision(costDetails?.gasCostDetails?.tariff?.price?.[costsType])} / ${
                      productUnits.gas
                    }`}
                  </Stack.Item>
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        )}

        {Number(warmthElectricity) > 0 && (
          <Stack.Item>
            <Stack direction="row" alignY="start" gap="2">
              <Stack.Item grow={true}>
                <Stack direction="row" alignY="center" gap="2">
                  <WarmthIcon color="iconHeat" />
                  <Text weight="bold">{textualData?.unitPriceLabels?.warmthLabel?.value}</Text>
                </Stack>
              </Stack.Item>

              <Stack.Item shrink={false}>
                <Stack direction="row" alignY="end" gap="2">
                  <Stack.Item grow={true}>
                    {`${currency.euroHighPrecision(
                      costDetails?.warmthEkvCostDetails?.tariff?.price?.[costsType] ??
                        costDetails?.warmthCostDetails?.tariff?.price?.[costsType],
                    )} / ${productUnits.warmth}`}
                  </Stack.Item>
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        )}

        {Number(warmthWater) > 0 && (
          <>
            <Stack.Item>
              <Stack direction="row" alignY="start" gap="2">
                <Stack.Item grow={true}>
                  <Stack direction="row" alignY="center" gap="2">
                    <WaterIcon color="iconWater" />
                    <Text weight="bold">{textualData?.unitPriceLabels?.waterColdLabel?.value}</Text>
                  </Stack>
                </Stack.Item>

                <Stack.Item shrink={false}>
                  <Stack direction="row" alignY="end" gap="2">
                    <Stack.Item grow={true}>
                      {`${currency.euroHighPrecision(
                        costDetails?.tapwaterCostDetails?.tariffcoldwater?.price?.[costsType],
                      )} / ${productUnits.tapWater}`}
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
              </Stack>
            </Stack.Item>

            <Stack.Item>
              <Stack direction="row" alignY="start" gap="2">
                <Stack.Item grow={true}>
                  <Stack direction="row" alignY="center" gap="2">
                    <WaterIcon color="iconWater" />
                    <Text weight="bold">{textualData?.unitPriceLabels?.waterWarmLabel?.value}</Text>
                  </Stack>
                </Stack.Item>

                <Stack.Item shrink={false}>
                  <Stack direction="row" alignY="end" gap="2">
                    <Stack.Item grow={true}>
                      {`${currency.euroHighPrecision(
                        costDetails?.tapwaterCostDetails?.tariffwarmwater?.price?.[costsType],
                      )} / ${productUnits.tapWater}`}
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
              </Stack>
            </Stack.Item>
          </>
        )}

        {Number(electricityRedeliveryRemainder) > 0 && (
          <Stack.Item>
            <Stack direction="row" alignY="start" gap="2">
              <Stack.Item grow={true}>
                <Stack direction="row" alignY="center" gap="2">
                  <RedeliveryIcon color="iconSolar" />
                  <Text weight="bold">{textualData?.unitPriceLabels?.redeliveryLabel?.value}</Text>
                </Stack>
              </Stack.Item>

              <Stack.Item shrink={false}>
                <Stack direction="row" alignY="end" gap="2">
                  <Stack.Item grow={true}>
                    {`${currency.euroHighPrecision(
                      costDetails?.electricityCostDetails?.redeliverytariff?.price?.[costsType],
                    )} / ${productUnits.electricity}`}
                  </Stack.Item>
                </Stack>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        )}

        <NettingSplitFeature>
          {isNettingSplitEnabled => isNettingSplitEnabled && <NettingDisclosureNote offer={offer} />}
        </NettingSplitFeature>

        <Box paddingTop={{ initial: '3', md: '0' }}>
          <Text size="BodyXS" color="textLowEmphasis">
            {tariffDetailsOverviewCopy.totalsExplanation}
          </Text>
        </Box>

        {checkOfferIncludesDynamicPricingProduct(offer) &&
          textualData?.unitPriceLabels?.estimatedTariffLabel?.value && (
            <Box paddingTop={{ initial: '3', md: '0' }}>
              <Text size="BodyXS" color="textLowEmphasis">
                {textualData?.unitPriceLabels?.estimatedTariffLabel?.value}
              </Text>
            </Box>
          )}
      </Stack>
    </Text>
  );
};
